import React, { useEffect } from 'react';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryParam } from 'use-query-params';

import { TENANTS, THEMED_TENANTS } from 'appConstants';
import { FEATURES } from 'appConstants';
import { SWITCH_TENANT_COMPLETE, TOKEN_SIGNIN } from 'containers/App/constants';
import IndexPage from 'containers/App/pages';
import ChatBot from 'containers/ChatBot';
import PublicCustomer from 'containers/Customer/pages';
import { useTenant, useTypedSelector } from 'hooks';
import { useFeatureFlags } from 'hooks';
import ForgetPassword from 'pages/public/ForgetPassword';
import InviteTenant from 'pages/public/InviteTenant';
import { DashboardLoading } from 'pages/public/Loading';
import NotFoundPage from 'pages/public/NotFoundPage';
import Registration from 'pages/public/Registration';
import ResetPassword from 'pages/public/ResetPassword';
import SignInPage from 'pages/public/SignInPage';

import RestrictedRoute from './RestrictedRoute';
import { getFilteredMenu } from './getMenu';
import ScrollToTop from './scrollToTop';
import { PerxState } from 'MyTypes';

const App = () => {
  const navigate = useNavigate();
  const history = {
    push: (to: any) => navigate(to),
    replace: (to: any) => navigate(to, { replace: true })
  };
  const location = useLocation();
  const dispatch = useDispatch();
  const [token] = useQueryParam('token');
  const { status, switcher, currentTheme, themes } = useThemeSwitcher();
  const permissions = useSelector(
    (state: PerxState) => state.permissions.permissions,
    shallowEqual
  );
  const switchingTenant = useTypedSelector(state => state?.global?.switchingTenant);
  const { name: tenantName } = useTenant();
  const menu = getFilteredMenu(permissions);

  const [code] = useQueryParam('code');
  const [codeTenant] = useQueryParam('tenant');
  const features = useFeatureFlags();
  const isCodeSignin = !!code && !!codeTenant;
  const perxAssistant = features[FEATURES.PERX_CONNECT.key];

  useEffect(() => {
    // initiate token login process if token query param detected
    // but if the path contains "customers", that means the user is trying
    // to access the Public Customer page, which doesn't need to perform token signin.
    const isPublicPage = location.pathname.startsWith('/customers');
    const isTokenSignin = !!token && !isPublicPage && !isCodeSignin;

    if (isTokenSignin) {
      const payload = {
        bearer_token: token,
        token: token,
        expires_at: null,
        password_expires_at: null,
        email: null
      };

      dispatch({ type: TOKEN_SIGNIN, payload: payload, location, history });
    }
    // eslint-disable-next-line
  }, [token, isCodeSignin]);

  useEffect(() => {
    if (switchingTenant) {
      history.push(menu.length > 0 ? menu[0].url : '/signin');
      dispatch({ type: SWITCH_TENANT_COMPLETE });
    }
    // eslint-disable-next-line
  }, [permissions]);

  // Set theme to Zeal if it is not set
  useEffect(() => {
    if (tenantName === TENANTS?.ZEAL && currentTheme !== themes.zeal) {
      switcher({ theme: themes.zeal });
    } else if (
      (tenantName === TENANTS?.RUSH || tenantName === TENANTS?.CWC) &&
      currentTheme !== themes.rush
    ) {
      switcher({ theme: themes.rush });
    } else if (tenantName === TENANTS?.AFFIRM && currentTheme !== themes.affirm) {
      switcher({ theme: themes.affirm });
    } else if (!THEMED_TENANTS.includes(tenantName) && currentTheme !== themes.light) {
      switcher({ theme: themes.light });
    }
  }, [tenantName, currentTheme, themes, switcher]);

  if (status === 'loading') {
    return <DashboardLoading />;
  }

  return (
    <ScrollToTop>
      <div
        style={{
          position: 'relative'
        }}
      >
        <div
          style={{
            position: 'fixed',
            zIndex: 1000,
            margin: '0 auto',
            left: '40%'
          }}
        >
          {perxAssistant && !location.pathname.includes('quicksight') && <ChatBot />}
        </div>
        <Routes>
          <Route
            path="/"
            element={
              <Navigate
                to={isCodeSignin ? `/signin?tenant=${codeTenant}&code=${code}` : '/p'}
                replace
              />
            }
          />
          <Route path="/p/*" element={<RestrictedRoute element={<IndexPage />} />} />
          {/* for StarHub */}
          <Route path="/customers/" element={<PublicCustomer />} />
          <Route path="/login" element={<Navigate to="/signin" replace />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/reset_password" element={<ResetPassword />} />
          <Route path="/invitation/accept" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgetPassword />} />
          <Route path="/set-password" element={<ForgetPassword />} />
          <Route path="/invite_tenant" element={<InviteTenant />} />
          <Route path="/registration" element={<Registration />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </ScrollToTop>
  );
};

export default App;
